import { useState, useEffect, useRef } from "react";
import { sb } from "../lib/supabase";
import { useIsMobile } from "../lib/useIsMobile";

// Settings → Fleet. Educates + sells the Bouncie GPS integration, then connects it in
// one click: the operator approves on Bouncie's own page (hosted OAuth) and lands back
// here connected — no API keys to copy. Disconnected = a one-scroll pitch ending in the
// Connect button; connected = a calm status panel.
const BOUNCIE_BUY = "https://www.bouncie.com/";

const BENEFITS = [
  ["🧾", "IRS-ready mileage", "Every trip's date, distance & route logged automatically — audit-proof deductions, no logbook."],
  ["📍", "Proof of service", "A GPS timestamp at each address ends “you never showed up” disputes for good."],
  ["⏱️", "Time on site", "See exactly how long each crew spent at a yard — catch rushed or skipped visits."],
  ["🚀", "Jobs start themselves", "Jobs auto-start when the truck arrives and complete when it leaves. No tapping."],
];

const HOW = [
  ["Bouncie tracks each trip", "Your plug-in GPS reports every drive from the truck."],
  ["iScoopCRM matches the stop", "We line each stop up to the client whose yard it is."],
  ["Miles logged, job started", "Mileage is filed and the visit is started for you — automatically."],
];

export function FleetSettings({ showToast }) {
  const isMobile = useIsMobile();
  const [status, setStatus] = useState(null);   // null = loading
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState("");
  const popupRef = useRef(null);
  const pollRef  = useRef(null);

  async function loadStatus() {
    try { const r = await sb.fn("connect-bouncie-account", { action: "status" }); setStatus(r || { connected: false }); return r; }
    catch { setStatus(s => s || { connected: false }); return null; }
  }
  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  useEffect(() => {
    loadStatus();
    return () => { stopPoll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On a phone the OAuth "popup" is really a new Safari tab, and setInterval is paused while
  // this tab is in the background — so the moment the owner switches back, re-check the
  // connection and release the button instead of leaving it stuck on "Opening Bouncie…".
  useEffect(() => {
    const onShow = async () => {
      if (document.visibilityState !== "visible" || !pollRef.current) return;
      const s = await loadStatus();
      if (s?.connected) { stopPoll(); try { popupRef.current && popupRef.current.close(); } catch {} setBusy(false); showToast && showToast("Bouncie connected ✓"); }
      else if (popupRef.current && popupRef.current.closed) { stopPoll(); setBusy(false); }
    };
    document.addEventListener("visibilitychange", onShow);
    window.addEventListener("pageshow", onShow);
    return () => { document.removeEventListener("visibilitychange", onShow); window.removeEventListener("pageshow", onShow); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open a popup synchronously (so it isn't blocked), then point it at the authorize URL.
  function connect() {
    setErr("");
    const popup = window.open("about:blank", "bouncie-connect", "width=520,height=720");
    if (!popup) { setErr("Please allow pop-ups for this site, then click Connect Bouncie again."); return; }
    popupRef.current = popup;
    setBusy(true);
    sb.fn("connect-bouncie-account", { action: "authorize_url" })
      .then(r => {
        if (r && r.url) { popup.location.href = r.url; startPoll(); }
        else { try { popup.close(); } catch {} setErr(r?.error || "Couldn't start the Bouncie connection. Try again."); setBusy(false); }
      })
      .catch(() => { try { popup.close(); } catch {} setErr("Couldn't reach the server. Try again."); setBusy(false); });
  }

  // Backup for when the postMessage is missed (popup closed manually, blocker, etc.).
  function startPoll() {
    stopPoll();
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks++;
      const popup = popupRef.current;
      if (popup && popup.closed) { stopPoll(); const s = await loadStatus(); setBusy(false); if (s?.connected) showToast && showToast("Bouncie connected ✓"); return; }
      if (ticks % 2 === 0) { const s = await loadStatus(); if (s?.connected) { stopPoll(); try { popup && popup.close(); } catch {} setBusy(false); showToast && showToast("Bouncie connected ✓"); } }
      if (ticks > 360) { stopPoll(); setBusy(false); }   // ~6 min cap
    }, 1000);
  }

  async function disconnect() {
    if (!window.confirm("Disconnect Bouncie?\n\nTrip syncing, mileage logs and auto-job suggestions will stop. Miles you've already logged stay.")) return;
    setBusy(true);
    try { await sb.fn("connect-bouncie-account", { action: "disconnect" }); showToast && showToast("Bouncie disconnected"); await loadStatus(); }
    catch { showToast && showToast("Couldn't disconnect — try again", "error"); }
    setBusy(false);
  }
  const copy = (text, msg) => { navigator.clipboard?.writeText(text).then(() => showToast && showToast(msg)).catch(() => {}); };

  if (status === null) return <div className="card"><div style={{ padding: 22, color: "var(--text2)" }}>Loading Fleet…</div></div>;

  return (
    <div className="fs">
      <style>{FS_CSS}</style>

      {status.connected ? (
        /* ───────────── CONNECTED — calm status panel ───────────── */
        <div className="fs-wrap">
          <div className="fs-cstatus">
            <span className="fs-pill on">● Connected</span>
            {status.connected_at && <span className="fs-since">since {new Date(status.connected_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>}
          </div>
          <div className="fs-h1 sm">Bouncie is connected</div>
          <p className="fs-lede sm">Your trucks’ trips are syncing into iScoopCRM. Your live map, routes and mileage live under the <b>Fleet</b> tab in the sidebar.</p>

          <div className="fs-stats">
            <div className="fs-stat"><div className="fs-statn">{status.vehicle_count ?? 0}</div><div className="fs-statl">Vehicles tracked</div></div>
            <div className="fs-stat"><div className="fs-statn">On</div><div className="fs-statl">IRS mileage logging</div></div>
            <div className="fs-stat"><div className="fs-statn">On</div><div className="fs-statl">Auto job suggestions</div></div>
          </div>

          {status.last_error && <div className="fs-warn">⚠ Last sync issue: {status.last_error}. Try Reconnect.</div>}

          <div className="fs-row">
            <button className="btn btn-ghost" disabled={busy} onClick={connect}>{busy ? "Opening Bouncie…" : "🔄 Reconnect"}</button>
          </div>
          <div className="fs-danger">
            <button className="fs-disconnect" disabled={busy} onClick={disconnect}>Disconnect Bouncie</button>
          </div>
        </div>
      ) : (
        /* ───────────── DISCONNECTED — pitch → one-click connect ───────────── */
        <div className="fs-wrap">
          <div className="fs-hero">
            <span className="fs-eyebrow">🚚 Fleet tracking</span>
            <div className="fs-h1">Log every mile and prove every visit — automatically</div>
            <p className="fs-lede">Connect your Bouncie GPS trackers and iScoopCRM turns each truck’s trips into IRS-ready mileage, proof-of-service timestamps, and jobs that start themselves when you arrive.</p>
            <span className="fs-pill off">○ Not connected</span>
          </div>

          {status.last_error && <div className="fs-warn">⚠ Last attempt didn’t finish: {status.last_error}. Click Connect Bouncie to try again.</div>}

          <div className="fs-benefits">
            {BENEFITS.map(([ic, t, d]) => (
              <div key={t} className="fs-benefit">
                <span className="fs-bicon">{ic}</span>
                <div><div className="fs-bt">{t}</div><div className="fs-bd">{d}</div></div>
              </div>
            ))}
          </div>

          <div className="fs-label">How it works</div>
          <div className="fs-how">
            {HOW.map(([t, d], i) => (
              <div key={t} className="fs-howstep">
                <span className="fs-hn">{i + 1}</span>
                <div><div className="fs-bt">{t}</div><div className="fs-bd">{d}</div></div>
              </div>
            ))}
          </div>

          <div className="fs-hr" />

          {/* one-click connect */}
          <div className="fs-connect">
            <div className="fs-label" style={{ marginBottom: 8 }}>Connect in under a minute</div>
            <ol className="fs-steps tight">
              <li>Click <b>Connect Bouncie</b> below.</li>
              <li>Sign in on Bouncie’s page and tap <b>Approve</b>.</li>
              <li>Done — your trucks start syncing.</li>
            </ol>
            <div className="fs-reassure">🔒 You sign in on Bouncie’s own page and approve read-only access. We never see your Bouncie password, and you can disconnect anytime.</div>
            {err && <div className="fs-err">{err}</div>}
            <div className="fs-row">
              <button className="btn btn-gold" disabled={busy} onClick={connect}>{busy ? "Opening Bouncie…" : "🔗 Connect Bouncie"}</button>
            </div>
            <div className="fs-buy">You’ll need an active Bouncie account with your tracker installed. No tracker yet? Bouncie plugs into your truck’s OBD-II port in about a minute. <a href={BOUNCIE_BUY} target="_blank" rel="noreferrer">See devices ↗</a></div>
          </div>

          {/* one-time platform setup (owner) */}
          {status.redirect_uri && (
            <details className="fs-owner">
              <summary>⚙️ One-time platform setup (owner only)</summary>
              <div className="fs-ownerbody">
                <p>Before operators can connect, register the iScoopCRM app in your Bouncie developer portal once and set its <b>Redirect URL</b> to:</p>
                <div className="fs-rrow">
                  <code className="fs-rurl">{status.redirect_uri}</code>
                  <button className={`btn btn-ghost ${isMobile ? "btn-sm" : "btn-xs"}`} onClick={() => copy(status.redirect_uri, "Redirect URL copied")}>📋 Copy</button>
                </div>
                <p style={{ marginTop: 10 }}>Then make sure the app’s <b>Client Secret</b> is set as the <code>BOUNCIE_CLIENT_SECRET</code> function secret in Supabase. Operators never see any of this.</p>
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

const FS_CSS = `
.fs-wrap{max-width:640px}
.fs-hero{margin-bottom:22px}
.fs-eyebrow{display:inline-block;font-size:11px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:var(--accent);margin-bottom:11px}
.fs-h1{font-size:25px;font-weight:700;letter-spacing:-.025em;line-height:1.14;color:var(--text);max-width:18ch}
.fs-h1.sm{font-size:21px;margin-top:4px}
.fs-lede{font-size:14.5px;color:var(--text2);line-height:1.55;margin:11px 0 14px;max-width:54ch}
.fs-lede.sm{margin-bottom:18px}
.fs-pill{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;padding:4px 11px;border-radius:999px}
.fs-pill.off{color:var(--text2);background:rgba(0,0,0,.05)}
.fs-pill.on{color:var(--ok);background:rgba(26,138,82,.12)}
.fs-cstatus{display:flex;align-items:center;gap:10px;margin-bottom:4px}
.fs-since{font-size:12.5px;color:var(--text2)}
.fs-benefits{display:grid;grid-template-columns:repeat(auto-fit,minmax(248px,1fr));gap:16px 22px;margin-bottom:24px}
.fs-benefit{display:flex;gap:12px;align-items:flex-start}
.fs-bicon{font-size:20px;line-height:1;flex-shrink:0}
.fs-bt{font-size:14px;font-weight:600;color:var(--text)}
.fs-bd{font-size:12.5px;color:var(--text2);line-height:1.45;margin-top:2px}
.fs-label{font-size:11px;font-weight:700;color:var(--text3);text-transform:uppercase;letter-spacing:.06em;margin-bottom:13px}
.fs-how{display:flex;flex-direction:column;gap:14px}
.fs-howstep{display:flex;gap:13px;align-items:flex-start}
.fs-hn{flex-shrink:0;width:26px;height:26px;border-radius:50%;background:var(--accent-weak);color:var(--accent);font-weight:700;font-size:13px;display:flex;align-items:center;justify-content:center}
.fs-hr{border-top:1px solid var(--border);margin:24px 0}
.fs-steps{margin:0 0 14px;padding-left:20px;display:flex;flex-direction:column;gap:8px}
.fs-steps.tight li{font-size:13.5px;color:var(--text);line-height:1.5}
.fs-reassure{font-size:12.5px;color:var(--text2);background:var(--bg);border:1px solid var(--border);border-radius:var(--r-sm,8px);padding:10px 13px;margin:4px 0 14px;line-height:1.5}
.fs-err{font-size:12.5px;color:var(--danger);background:rgba(215,50,43,.07);border:1px solid rgba(215,50,43,.25);border-radius:var(--r-sm,8px);padding:10px 13px;margin-bottom:13px;line-height:1.45}
.fs-warn{font-size:12.5px;color:#974f0c;background:#fffae6;border:1px solid #ffe7a3;border-radius:var(--r-sm,8px);padding:10px 13px;margin:0 0 16px;line-height:1.45}
.fs-row{display:flex;gap:9px;align-items:center;flex-wrap:wrap}
.fs-buy{font-size:12.5px;color:var(--text2);margin-top:16px;line-height:1.5}
.fs-buy a{color:var(--accent);font-weight:600;text-decoration:none;white-space:nowrap}
.fs-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:12px;margin:6px 0 4px}
.fs-stat{background:var(--bg);border:1px solid var(--border);border-radius:var(--r,12px);padding:14px 16px}
.fs-statn{font-size:22px;font-weight:700;color:var(--text);letter-spacing:-.02em}
.fs-statl{font-size:12px;color:var(--text2);margin-top:2px}
.fs-danger{margin-top:22px;border-top:1px solid var(--border);padding-top:16px}
.fs-disconnect{background:none;border:none;color:var(--danger);font-size:12.5px;font-weight:600;cursor:pointer;padding:0}
.fs-disconnect:hover{text-decoration:underline}
.fs-owner{margin-top:22px;border-top:1px solid var(--border);padding-top:14px}
.fs-owner>summary{font-size:12.5px;font-weight:600;color:var(--text2);cursor:pointer;list-style:none}
.fs-owner>summary::-webkit-details-marker{display:none}
.fs-ownerbody{font-size:12.5px;color:var(--text2);line-height:1.55;margin-top:10px}
.fs-ownerbody code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11.5px;background:var(--bg);border:1px solid var(--border);border-radius:5px;padding:1px 5px}
.fs-rrow{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:6px}
.fs-rurl{flex:1;min-width:220px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px;color:var(--text);background:var(--bg);border:1px solid var(--border);border-radius:8px;padding:8px 11px;overflow:auto;white-space:nowrap}
/* phones: thumb-sized targets, no hover affordances, nothing nowrap */
@media (max-width:768px){
  .fs-disconnect{padding:10px 0;min-height:44px;font-size:13.5px;text-decoration:underline;text-underline-offset:3px}
  .fs-row .btn{width:100%;justify-content:center}
  .fs-rurl{min-width:0;flex-basis:100%;white-space:normal;word-break:break-all;overflow:visible}
  .fs-rrow .btn{width:100%;justify-content:center}
  .fs-buy a{white-space:normal}
  .fs-h1{max-width:none}
}
`;
